// Servicio para manejar módulos y planes en Olimpo Web
import { API_BASE_URL } from '../config/api';

class ModuleService {
  constructor() {
    this.apiBase = API_BASE_URL;
  }

  // Obtener token de autenticación
  getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  // ===== MÉTODOS PARA PLANES =====

  // Obtener todos los planes disponibles
  async getPlans() {
    try {
      const response = await fetch(`${this.apiBase}/api/plans/`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo planes: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getPlans:', error);
      throw error;
    }
  }

  // Obtener plan de un site específico
  async getSitePlan(siteId) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/plan`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo plan del site: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getSitePlan:', error);
      throw error;
    }
  }

  // Actualizar plan de un site
  async updateSitePlan(siteId, planId) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/plan`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ plan_id: planId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error actualizando plan del site: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en updateSitePlan:', error);
      throw error;
    }
  }

  // ===== MÉTODOS PARA MÓDULOS =====

  // Obtener todos los módulos disponibles
  async getModules() {
    try {
      const response = await fetch(`${this.apiBase}/api/modules/`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo módulos: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getModules:', error);
      throw error;
    }
  }

  // Obtener módulos habilitados de un site específico
  async getSiteModules(siteId) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/modules`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo módulos del site: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getSiteModules:', error);
      throw error;
    }
  }

  // Habilitar/deshabilitar módulo para un site
  async toggleSiteModule(siteId, moduleId, enabled) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/modules/${moduleId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ enabled })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error actualizando módulo del site: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en toggleSiteModule:', error);
      throw error;
    }
  }

  // Actualizar múltiples módulos de un site
  async updateSiteModules(siteId, modules) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/modules`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ modules })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error actualizando módulos del site: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en updateSiteModules:', error);
      throw error;
    }
  }

  // ===== CONSTANTES Y UTILIDADES =====

  // Definición estática de módulos y sus planes asociados
  getModuleDefinitions() {
    return {
      MEMBERS: { name: 'Gestión de Miembros', plan: 'BASIC', description: 'Administra miembros, perfiles y membresías' },
      LOCATIONS: { name: 'Ubicaciones', plan: 'INTERMEDIATE', description: 'Gestiona múltiples ubicaciones de práctica' },
      TIME_SCHEDULES: { name: 'Horarios', plan: 'INTERMEDIATE', description: 'Organiza horarios de clases y entrenamientos' },
      EVENTS: { name: 'Eventos', plan: 'INTERMEDIATE', description: 'Crea y gestiona eventos especiales' },
      FEED: { name: 'Feed de Actividades', plan: 'INTERMEDIATE', description: 'Mantén informados a tus miembros' },
      BILLING_MODELS: { name: 'Modelos de Facturación', plan: 'PRO', description: 'Gestión avanzada de pagos y facturación' },
      CUSTOM_DOMAIN: { name: 'Dominio Personalizado', plan: 'PRO', description: 'Usa tu propio dominio para tu sitio' }
    };
  }

  // Definición estática de planes y precios
  getPlanDefinitions() {
    return {
      BASIC: { name: 'Básico', price: 0, currency: 'USD', period: 'month', features: ['MEMBERS'] },
      INTERMEDIATE: { name: 'Intermedio', price: 10, currency: 'USD', period: 'month', features: ['MEMBERS', 'LOCATIONS', 'TIME_SCHEDULES', 'EVENTS', 'FEED'] },
      PRO: { name: 'Profesional', price: 50, currency: 'USD', period: 'month', features: ['MEMBERS', 'LOCATIONS', 'TIME_SCHEDULES', 'EVENTS', 'FEED', 'BILLING_MODELS', 'CUSTOM_DOMAIN'] }
    };
  }

  // Verificar si un módulo está habilitado para un plan
  isModuleAvailableForPlan(moduleId, planId) {
    const planDefinitions = this.getPlanDefinitions();
    const plan = planDefinitions[planId];
    return plan && plan.features.includes(moduleId);
  }

  // Obtener módulos disponibles para un plan
  getAvailableModulesForPlan(planId) {
    const planDefinitions = this.getPlanDefinitions();
    const plan = planDefinitions[planId];
    return plan ? plan.features : [];
  }
}

const moduleService = new ModuleService();
export default moduleService;
